import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useMemo,
} from "react";
import { Card, Tabs, Empty, Spin, message, Switch } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import CreateWelcomeTemplate from "./CreateWelcomeTemplate";
import apiFactory from "../../../api";
import WelcomeCarousel from "../../../components/carousel/WelcomeCarosel";
import "./style.scss";
import { getFileExtension } from "../../../utils/Utils";
import { conversationType, MESSAGE_STATUS } from "../../../config/Constant";
import { v4 as uuidv4 } from "uuid";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { uploadFileV2 } from "../../../utils/SendFile";

const { TabPane } = Tabs;

const BASE_WIDTH = 1920;
const BASE_HEIGHT = 940;
const MAX_SELECTED = 4;

const PreviewScale = React.memo(({ children }) => {
  const ref = useRef(null);
  const scaleRef = useRef(0);
  const [, forceUpdate] = useState(0);

  useLayoutEffect(() => {
    const calcScale = () => {
      if (!ref.current) return;

      const width = ref.current.offsetWidth;
      const newScale = width / BASE_WIDTH;

      if (Math.abs(scaleRef.current - newScale) > 0.001) {
        scaleRef.current = newScale;
        forceUpdate((x) => x + 1);
      }
    };

    calcScale();
    window.addEventListener("resize", calcScale);
    return () => window.removeEventListener("resize", calcScale);
  }, []);

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ aspectRatio: `${BASE_WIDTH} / ${BASE_HEIGHT}` }}
    >
      <div
        className="absolute top-0 left-0 origin-top-left"
        style={{
          width: BASE_WIDTH,
          height: BASE_HEIGHT,
          transform: `scale(${scaleRef.current})`,
          willChange: "transform",
          visibility: scaleRef.current === 0 ? "hidden" : "visible",
        }}
      >
        {children}
      </div>
    </div>
  );
});

const SlideItem = React.memo(
  ({
    slide,
    isChecked,
    order,
    onToggle,
    isActive,
    onSelect,
    handleDeleteSlide,
    handleEditSlide,
  }) => {
    return (
      <div
        className={`relative group rounded-md overflow-hidden border-2 transition-all ${
          isActive ? "border-blue-500 shadow-lg" : "border-transparent"
        }`}
      >
        <div
          className={`absolute top-2 left-2 z-20 w-6 h-6 flex items-center justify-center  cursor-pointer rounded-full border-2 transition-colors ${
            isChecked
              ? "bg-blue-600 border-blue-600 text-white"
              : "bg-white/50 border-white text-transparent"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(slide);
          }}
        >
          {isChecked ? order : ""}
        </div>

        {onSelect && isChecked ? (
          <button
            type="button"
            className="absolute top-2 right-2 z-20 bg-red-500 text-white w-7 h-7 rounded hover:bg-red-600 flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(slide);
            }}
            title="Remove slide"
          >
            ✕
          </button>
        ) : (
          <div className="absolute top-2 right-2 z-20 flex gap-1">
            {/* Edit */}
            <button
              type="button"
              className="bg-yellow-400 text-white w-7 h-7 rounded hover:bg-yellow-500 flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                handleEditSlide?.(slide);
              }}
              title="Edit slide"
            >
              ✎
            </button>

            {/* Delete */}
            <button
              type="button"
              className="bg-red-500 text-white w-7 h-7 rounded hover:bg-red-600 flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSlide?.(slide?.slideId);
              }}
              title="Delete slide"
            >
              <DeleteOutlined />
            </button>
          </div>
        )}

        <PreviewScale>
          <WelcomeCarousel slides={[slide]} />
        </PreviewScale>
      </div>
    );
  },
);

/* ================= MAIN COMPONENT ================= */
export default function WelcomeManagement() {
  const [slideGroups, setSlideGroups] = useState([]);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [activeScreen, setActiveScreen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState("1");
  const [editingSlide, setEditingSlide] = useState(null);

  const getActiveTemplate = async () => {
    try {
      const welcomeTemplates = await apiFactory.welcomeSlideApi.getTemplate();
      if (welcomeTemplates?.status !== 200) {
        console.error("Failed to fetch welcome templates");
      }

      setSelectedScreens(welcomeTemplates?.data?.slides || []);
    } catch (error) {
      console.error("Error fetching welcome templates", error);
    }
  };

  useEffect(() => {
    getActiveTemplate();
  }, []);

  const handleUploadMusic = async (file) => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async () => {
      try {
        const controller = new AbortController();
        const res = await uploadFileV2(file, null, "FREE", controller.signal);
        if (res?.resourceId) {
          updateSelectedData("music", {
            resourceId: res?.resourceId,
            fileName: file.name,
          });
        }
      } catch (e) {
        console.error("Upload music error", e);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveMusic = () => {
    updateSelectedData("music", null);
  };

  const handleSaveScreens = async () => {
    if (selectedScreens.length === 0) {
      message.warning("No screen selected");
      return;
    }

    const { startDate, endDate } = selectedScreens[0] || {};

    if (!startDate || !endDate) {
      message.warning("Please select display time");
      return;
    }

    const payload = buildSavePayload();
    try {
      const res = await apiFactory.welcomeSlideApi.createTemplate(payload);
      if (res?.status === 200) message.success("Screens saved successfully");
    } catch (e) {
      message.error("Failed to save screens");
      console.error(e);
    }
  };

  const buildSavePayload = () => ({
    startDate: selectedScreens[0]?.startDate || null,
    endDate: selectedScreens[0]?.endDate || null,
    musicResourceId: selectedScreens[0]?.music?.resourceId || null,
    autoPlayMusic: !!selectedScreens[0]?.autoPlayMusic,
    isDefault: !!selectedScreens[0]?.isDefault,

    slides: selectedScreens.map((s) => ({
      slideId: s.slideId,
      order: s.order,
    })),
  });

  const toggleScreen = useCallback((slide) => {
    setSelectedScreens((prev) => {
      const exists = prev.find((s) => s.slideId === slide.slideId);
      if (!exists && prev.length >= MAX_SELECTED) {
        message.warning(`You can select up to ${MAX_SELECTED} screens only.`);
        return prev;
      }

      if (exists) {
        const filtered = prev.filter((s) => s.slideId !== slide.slideId);
        return filtered.map((s, i) => ({ ...s, order: i + 1 }));
      }

      return [
        ...prev,
        {
          ...slide,
          title: slide.titleEn,
          description: slide.descriptionEn,
          order: prev.length + 1,
        },
      ];
    });
  }, []);

  const updateSelectedData = (key, value) => {
    setSelectedScreens((prev) => prev.map((s) => ({ ...s, [key]: value })));
  };

  const handleEditSlide = useCallback((slide) => {
    setEditingSlide(slide);
    setCurrentTab("2");
  }, []);

  const ratio = selectedScreens.length / MAX_SELECTED;

  const highlightClass =
    ratio === 1
      ? "border-green-300 bg-green-50"
      : ratio >= 0.8
        ? "border-yellow-300 bg-yellow-50"
        : "border-blue-200 bg-blue-50";

  const handleDeleteSlide = useCallback(async (slideId) => {
    try {
      const res = await apiFactory.welcomeSlideApi.deleteSlide(slideId);

      if (res?.status !== 200) {
        message.error("Failed to delete slide");
        return;
      }

      message.success("Slide deleted successfully");

      setSlideGroups((prev) =>
        prev
          .map((group) => ({
            ...group,
            slides: group.slides.filter((s) => s.slideId !== slideId),
          }))
          .filter((group) => group.slides.length > 0),
      );

      setSelectedScreens((prev) =>
        prev
          .filter((s) => s.slideId !== slideId)
          .map((s, i) => ({ ...s, order: i + 1 })),
      );
    } catch (error) {
      console.error("delete slide error", error);
      message.error("Failed to delete slide");
    }
  }, []);

  useEffect(() => {
    const fetchSlides = async () => {
      setLoading(true);
      try {
        const res = await apiFactory.welcomeSlideApi.getSlides("");
        if (res?.status === 200) {
          setSlideGroups(res.data || []);
        }
      } finally {
        setLoading(false);
      }
    };

    if (currentTab === "1") {
      fetchSlides();
      setEditingSlide(null);
    }
  }, [currentTab]);

  useEffect(() => {
    if (selectedScreens.length > 0 && !selectedScreens[0].startDate) {
      updateSelectedData("startDate", dayjs());
    }
  }, [selectedScreens]);

  /* ================= RENDER ================= */
  return (
    <div className="bg-gray-50 h-screen overflow-hidden welcome-management-page">
      <Tabs
        activeKey={currentTab}
        onChange={(key) => setCurrentTab(key)}
        className="bg-white h-full flex flex-col p-4 pt-0 rounded-lg shadow-sm"
      >
        <TabPane
          tab="Welcome Screens"
          key="1"
          className="flex-1 flex flex-col overflow-hidden"
        >
          <Card
            title={
              <div className="flex justify-between items-center w-full">
                <span className="font-semibold">Active Screens</span>

                <span
                  className={`text-sm font-semibold px-2 py-0.5 rounded-full
          ${
            ratio === 1
              ? "bg-red-100 text-red-600"
              : ratio >= 0.8
                ? "bg-yellow-100 text-yellow-700"
                : "bg-blue-100 text-blue-600"
          }`}
                >
                  {selectedScreens.length} / {MAX_SELECTED}
                </span>
              </div>
            }
            className={`mb-4 shadow-sm px-2 shrink-0 border-2 transition-all ${highlightClass}`}
          >
            {selectedScreens.length === 0 ? (
              <Empty description="No screens selected" className="h-[241px]" />
            ) : (
              <div className="space-y-6 h-auto">
                <div className=" grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {selectedScreens.map((slide) => (
                    <SlideItem
                      key={`selected-${slide.slideId}`}
                      slide={slide}
                      isChecked
                      order={slide.order}
                      onToggle={toggleScreen}
                      isActive={activeScreen?.slideId === slide.slideId}
                      onSelect={setActiveScreen}
                    />
                  ))}
                </div>

                <div className="grid md:grid-cols-5 gap-4 border-t border-gray-100 !mt-0">
                  {/* ===== MUSIC ===== */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">
                      Background Music
                    </label>
                    <div
                      className="flex items-center gap-2 h-10 px-3
                        rounded-md border border-dashed border-blue-300
                        bg-blue-50 text-blue-700
                        hover:bg-blue-100 transition"
                    >
                      <label className="flex-1 flex items-center gap-2 cursor-pointer overflow-hidden">
                        <span className="truncate text-sm font-medium">
                          {selectedScreens[0]?.music?.fileName
                            ? `🎵 ${selectedScreens[0].music.fileName}`
                            : "Choose music"}
                        </span>
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={(e) => handleUploadMusic(e.target.files[0])}
                        />
                      </label>

                      {selectedScreens[0]?.music && (
                        <button
                          type="button"
                          onClick={handleRemoveMusic}
                          className="w-6 h-6 rounded-full bg-red-100 text-red-600
          hover:bg-red-200 flex items-center justify-center text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ===== START DATE ===== */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">
                      Start Date
                    </label>
                    <DatePicker
                      className="h-10 w-full"
                      value={
                        selectedScreens[0]?.startDate
                          ? dayjs(selectedScreens[0].startDate)
                          : dayjs()
                      }
                      onChange={(date) => updateSelectedData("startDate", date)}
                      allowClear={false}
                    />
                  </div>

                  {/* ===== END DATE ===== */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">
                      End Date
                    </label>
                    <DatePicker
                      className="h-10 w-full"
                      value={
                        selectedScreens[0]?.endDate
                          ? dayjs(selectedScreens[0].endDate)
                          : null
                      }
                      onChange={(date) => updateSelectedData("endDate", date)}
                      disabledDate={(current) =>
                        current &&
                        selectedScreens[0]?.startDate &&
                        current.isBefore(
                          dayjs(selectedScreens[0].startDate),
                          "day",
                        )
                      }
                    />
                  </div>

                  {/* ===== DEFAULT ===== */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">
                      Default
                    </label>
                    <div
                      className="h-10 px-3 rounded-md border border-gray-200
      bg-gray-50 flex items-center justify-between"
                    >
                      <span className="text-sm text-gray-700">Template</span>
                      <Switch
                        checked={!!selectedScreens[0]?.isDefault}
                        onChange={(checked) =>
                          updateSelectedData("isDefault", checked)
                        }
                      />
                    </div>
                  </div>

                  {/* ===== SAVE ===== */}
                  <div className="flex items-end">
                    <button
                      onClick={handleSaveScreens}
                      className="w-full h-10 rounded-md bg-blue-600 text-white
                      font-semibold text-sm
                      hover:bg-blue-700 active:scale-[0.98]
                      transition"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>

          <Card
            title="Design Library"
            className="flex-1 overflow-y-auto shadow-sm border-gray-100 px-2"
          >
            {loading ? (
              <div className="flex justify-center py-20">
                <Spin size="large" />
              </div>
            ) : slideGroups.length === 0 ? (
              <Empty />
            ) : (
              slideGroups.map((group) => (
                <div key={group.themeName} className="mb-6">
                  <h3 className="text-lg font-bold mb-1 text-gray-700 border-l-4 border-blue-500 pl-3">
                    {group.themeName || "Untitled Theme"}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                    {group.slides.map((slide) => {
                      const selected = selectedScreens.find(
                        (s) => s.slideId === slide.slideId,
                      );
                      return (
                        <SlideItem
                          key={slide.slideId}
                          slide={slide}
                          isChecked={!!selected}
                          order={selected?.order}
                          onToggle={toggleScreen}
                          handleDeleteSlide={handleDeleteSlide}
                          handleEditSlide={handleEditSlide}
                        />
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <PlusOutlined />
              {editingSlide ? " Edit Design" : " Create New Design"}
            </span>
          }
          key="2"
          onChange={(key) => setCurrentTab(key)}
          className="flex-1 overflow-hidden"
        >
          <div className="h-full overflow-y-auto pr-2">
            <CreateWelcomeTemplate
              activeTab={currentTab}
              editingSlide={editingSlide}
              onFinishEdit={(updated) => {
                const updatedSelectedScreens = selectedScreens.map((s) =>
                  s.slideId === editingSlide.slideId
                    ? {
                        ...s,
                        ...updated,
                      }
                    : s,
                );

                setSelectedScreens(updatedSelectedScreens);

                setEditingSlide(null);
              }}
            />
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
}
